import { selector } from 'recoil';
import {
  deviceListStateAtom,
  devicesSortingAtom,
  downloadDeviceDataModeAtom,
  filteredDeviceListSelector,
  filteredDeviceDataSelector,
  selectedDeviceStateAtom,
} from '../atoms/device_atoms';
import { labelsSelector } from './company_selectors';
import { getComparator } from '../../utils/sorting_helpers';
import type {
  ArchivedSession,
  deviceListEntryInterface,
} from '../interfaces/device_interfaces';
import { formatDeviceKey } from '../../utils/deviceKey';
import maxBy from 'lodash/maxBy';

export const maxWaterLevelSelector = selector({
  key: 'maxWaterLevelSelector',
  get: ({ get }) => {
    const selectedDeviceData = get(filteredDeviceDataSelector);
    if (!selectedDeviceData || !selectedDeviceData.water_data.length) {
      return null;
    }

    const maxPoint = maxBy(selectedDeviceData.water_data, 'water_level_mm');
    if (!maxPoint || maxPoint.water_level_mm === -1) {
      return null;
    }

    return {
      value: maxPoint.water_level_mm,
      timestamp: maxPoint.measurement_unix_timestamp,
    };
  },
});

export const maxRainfallLevelSelector = selector({
  key: 'maxRainfallSelector',
  get: ({ get }) => {
    const selectedDeviceData = get(filteredDeviceDataSelector);
    if (!selectedDeviceData || !selectedDeviceData.rainfall_data.length) {
      return null;
    }

    const maxPoint = maxBy(
      selectedDeviceData.rainfall_data,
      'rainfall_level_mm',
    );
    if (!maxPoint || maxPoint.rainfall_level_mm === -1) {
      return null;
    }

    return {
      value: maxPoint.rainfall_level_mm,
      timestamp: maxPoint.measurement_unix_timestamp,
    };
  },
});

export const deviceSessionsSelector = selector<ArchivedSession[]>({
  key: 'deviceSessionsSelector',
  get: ({ get }) => {
    const selectedDevice = get(selectedDeviceStateAtom);
    if (!selectedDevice) {
      return [];
    }

    return selectedDevice.archived_sessions;
  },
});

const EXCLUDED_LABELS = new Set([
  'Low battery',
  'No messages',
  'High ROC',
  'High Level',
  'blockage',
  'silt',
  'flood',
  'infiltration',
  'In Weather Mode',
]);

/**
 * List of labels that can be added/removed from devices
 */
export const addableLabelsSelector = selector({
  key: 'addableLabelsSelector',
  get: ({ get }) => {
    const labels = get(labelsSelector);
    return labels.filter(label => !EXCLUDED_LABELS.has(label));
  },
});

export const sortedDevicesSelector = selector({
  key: 'sortedDevicesSelector',
  get: ({ get }) => {
    const { devices } = get(filteredDeviceListSelector);
    const { sortBy, sortDirection } = get(devicesSortingAtom);

    return devices.slice().sort(getComparator(sortDirection, sortBy));
  },
});

// Selector to get a map of devices by their IDs
export const devicesMapSelector = selector({
  key: 'devicesMapSelector',
  get: ({ get }) => {
    const { devices } = get(deviceListStateAtom);
    return new Map(
      devices.map(device => [
        formatDeviceKey(device.device_id, device.session_id),
        device,
      ]),
    );
  },
});

// Selector to get a map of all (live + archived) devices by their IDs
export const allDevicesMapSelector = selector({
  key: 'allDevicesMapSelector',
  get: ({ get }) => {
    const { devices, archivedDevices } = get(deviceListStateAtom);
    return new Map(
      [...devices, ...archivedDevices].map(device => [
        formatDeviceKey(device.device_id, device.session_id),
        device,
      ]),
    );
  },
});

export const selectableDevicesSelector = selector<deviceListEntryInterface[]>({
  key: 'selectableDevicesSelector',
  get: ({ get }) => {
    const { devices, archivedDevices } = get(deviceListStateAtom);
    const mode = get(downloadDeviceDataModeAtom);

    return mode === 'live' ? devices : [...devices, ...archivedDevices];
  },
});
